/**
 * Out-of-box debug pages for common debugging scenarios.
 * These pages are automatically registered when using the debug system.
 */

import type { IDebugPage, SystemDebugInfo, EntityDebugInfo, DebugLogEntry } from '@gotti/core';
import { BaseDebugPage } from './BaseDebugPage.js';
import type { ClientDebugSystem } from './ClientDebugSystem.js';

// ========================================
// Systems Debug Page
// ========================================

/**
 * Debug page showing all registered systems and their state/logs.
 */
export class SystemsDebugPage extends BaseDebugPage {
  private systemsList?: HTMLDivElement;
  private logsPanel?: HTMLPreElement;
  private statePanel?: HTMLPreElement;
  private selectedSystem?: string;
  private refreshInterval?: ReturnType<typeof setInterval>;

  constructor(debugSystem: ClientDebugSystem) {
    super(
      { id: 'systems', label: 'Systems', priority: 10 },
      debugSystem
    );
  }

  createElement(): HTMLElement {
    const container = this.createContainer({ display: 'flex', height: '100%' });

    // Left panel - systems list
    const leftPanel = document.createElement('div');
    Object.assign(leftPanel.style, {
      width: '150px',
      borderRight: '1px solid #444',
      overflowY: 'auto',
      flexShrink: '0',
    });

    const listHeader = document.createElement('h4');
    listHeader.textContent = 'Systems';
    Object.assign(listHeader.style, {
      margin: '0 0 10px 0',
      padding: '5px',
      backgroundColor: '#333',
    });
    leftPanel.appendChild(listHeader);

    this.systemsList = document.createElement('div');
    leftPanel.appendChild(this.systemsList);
    container.appendChild(leftPanel);

    // Right panel - details
    const rightPanel = document.createElement('div');
    Object.assign(rightPanel.style, {
      flex: '1',
      padding: '10px',
      overflowY: 'auto',
    });

    // Tab buttons
    const tabBar = document.createElement('div');
    Object.assign(tabBar.style, { marginBottom: '10px' });
    
    const stateTab = this.createButton('State', () => this.showStatePanel(), { marginRight: '5px' });
    const logsTab = this.createButton('Logs', () => this.showLogsPanel());
    tabBar.appendChild(stateTab);
    tabBar.appendChild(logsTab);
    rightPanel.appendChild(tabBar);

    // Details panel (state)
    this.statePanel = document.createElement('pre');
    Object.assign(this.statePanel.style, {
      backgroundColor: '#1a1a1a',
      padding: '10px',
      borderRadius: '4px',
      overflow: 'auto',
      maxHeight: '300px',
      fontSize: '11px',
      whiteSpace: 'pre-wrap',
    });
    rightPanel.appendChild(this.statePanel);

    // Logs panel
    this.logsPanel = document.createElement('pre');
    Object.assign(this.logsPanel.style, {
      backgroundColor: '#1a1a1a',
      padding: '10px',
      borderRadius: '4px',
      overflow: 'auto',
      maxHeight: '300px',
      fontSize: '11px',
      display: 'none',
      whiteSpace: 'pre-wrap',
    });
    rightPanel.appendChild(this.logsPanel);

    container.appendChild(rightPanel);

    this.element = container;
    this.refresh();
    return container;
  }

  onActivate(): void {
    this.refreshInterval = setInterval(() => this.refresh(), 1000);
  }

  onDeactivate(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private refresh(): void {
    if (!this.systemsList) return;

    const systems = this.debugSystem.getAllSystemDebugInfo();
    this.systemsList.innerHTML = '';

    for (const [name, info] of Object.entries(systems)) {
      const item = document.createElement('div');
      Object.assign(item.style, {
        padding: '5px',
        cursor: 'pointer',
        backgroundColor: this.selectedSystem === name ? '#444' : 'transparent',
        borderBottom: '1px solid #333',
      });
      
      const typeIcon = info.systemType === 'server' ? '🖥' : '💻';
      item.textContent = `${typeIcon} ${name}`;
      item.onclick = () => this.selectSystem(name);
      this.systemsList.appendChild(item);
    }

    // Refresh selected system details
    if (this.selectedSystem) {
      this.updateDetails(systems[this.selectedSystem]);
    }
  }

  private selectSystem(name: string): void {
    this.selectedSystem = name;
    this.refresh();
  }

  private updateDetails(info?: SystemDebugInfo): void {
    if (!this.statePanel || !this.logsPanel) return;

    if (!info) {
      this.statePanel.textContent = 'Select a system';
      this.logsPanel.textContent = '';
      return;
    }

    // State
    const stateData = {
      name: info.name,
      type: info.systemType,
      state: info.state,
      properties: info.properties,
      stateBySource: info.stateBySource,
      propertiesBySource: info.propertiesBySource,
    };
    this.statePanel.textContent = JSON.stringify(stateData, null, 2);

    // Logs
    const logs = info.logs.slice(-50).reverse();
    this.logsPanel.innerHTML = '';
    for (const log of logs) {
      const logLine = document.createElement('div');
      logLine.style.color = this.getLogLevelColor(log.level);
      logLine.textContent = this.formatLogEntry(log);
      this.logsPanel.appendChild(logLine);
    }
  }

  private showStatePanel(): void {
    if (this.statePanel) this.statePanel.style.display = 'block';
    if (this.logsPanel) this.logsPanel.style.display = 'none';
  }

  private showLogsPanel(): void {
    if (this.statePanel) this.statePanel.style.display = 'none';
    if (this.logsPanel) this.logsPanel.style.display = 'block';
  }
}

// ========================================
// Entities Debug Page
// ========================================

/**
 * Debug page showing all entities and their state/logs.
 */
export class EntitiesDebugPage extends BaseDebugPage {
  private entitiesList?: HTMLDivElement;
  private detailsPanel?: HTMLDivElement;
  private selectedEntity?: string;
  private refreshInterval?: ReturnType<typeof setInterval>;

  constructor(debugSystem: ClientDebugSystem) {
    super(
      { id: 'entities', label: 'Entities', priority: 20 },
      debugSystem
    );
  }

  createElement(): HTMLElement {
    const container = this.createContainer({ display: 'flex', height: '100%' });

    // Left panel - entities list
    const leftPanel = document.createElement('div');
    Object.assign(leftPanel.style, {
      width: '150px',
      borderRight: '1px solid #444',
      overflowY: 'auto',
      flexShrink: '0',
    });

    const listHeader = document.createElement('h4');
    listHeader.textContent = 'Entities';
    Object.assign(listHeader.style, {
      margin: '0 0 10px 0',
      padding: '5px',
      backgroundColor: '#333',
    });
    leftPanel.appendChild(listHeader);

    this.entitiesList = document.createElement('div');
    leftPanel.appendChild(this.entitiesList);
    container.appendChild(leftPanel);

    // Right panel - details
    this.detailsPanel = document.createElement('div');
    Object.assign(this.detailsPanel.style, {
      flex: '1',
      padding: '10px',
      overflowY: 'auto',
    });
    container.appendChild(this.detailsPanel);

    this.element = container;
    this.refresh();
    return container;
  }

  onActivate(): void {
    this.refreshInterval = setInterval(() => this.refresh(), 1000);
  }

  onDeactivate(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private refresh(): void {
    if (!this.entitiesList) return;

    const entities = this.debugSystem.getAllEntityDebugInfo();
    this.entitiesList.innerHTML = '';

    for (const [id, info] of Object.entries(entities)) {
      const item = document.createElement('div');
      Object.assign(item.style, {
        padding: '5px',
        cursor: 'pointer',
        backgroundColor: this.selectedEntity === id ? '#444' : 'transparent',
        borderBottom: '1px solid #333',
        fontSize: '11px',
      });
      item.textContent = `${info.entityType}: ${id}`;
      item.onclick = () => this.selectEntity(id);
      this.entitiesList.appendChild(item);
    }

    // Refresh selected entity details
    if (this.selectedEntity) {
      this.updateDetails(entities[this.selectedEntity]);
    }
  }

  private selectEntity(id: string): void {
    this.selectedEntity = id;
    this.refresh();
  }

  private updateDetails(info?: EntityDebugInfo): void {
    if (!this.detailsPanel) return;

    if (!info) {
      this.detailsPanel.innerHTML = '<p>Select an entity</p>';
      return;
    }

    this.detailsPanel.innerHTML = '';

    // Header
    const header = document.createElement('h3');
    header.textContent = `${info.entityType} (${info.id})`;
    header.style.margin = '0 0 10px 0';
    this.detailsPanel.appendChild(header);

    // State section
    const stateSection = this.createSection('State');
    const statePre = document.createElement('pre');
    Object.assign(statePre.style, {
      backgroundColor: '#1a1a1a',
      padding: '10px',
      borderRadius: '4px',
      fontSize: '11px',
      maxHeight: '150px',
      overflow: 'auto',
    });
    statePre.textContent = JSON.stringify(info.state, null, 2);
    stateSection.appendChild(statePre);
    this.detailsPanel.appendChild(stateSection);

    // Properties section
    const propsSection = this.createSection('Properties');
    const propsPre = document.createElement('pre');
    Object.assign(propsPre.style, {
      backgroundColor: '#1a1a1a',
      padding: '10px',
      borderRadius: '4px',
      fontSize: '11px',
      maxHeight: '150px',
      overflow: 'auto',
    });
    propsPre.textContent = JSON.stringify(info.properties, null, 2);
    propsSection.appendChild(propsPre);
    this.detailsPanel.appendChild(propsSection);

    // Logs section
    const logsSection = this.createSection('Logs');
    const logsList = document.createElement('pre');
    Object.assign(logsList.style, {
      backgroundColor: '#1a1a1a',
      padding: '10px',
      borderRadius: '4px',
      fontSize: '11px',
      maxHeight: '150px',
      overflow: 'auto',
    });
    
    const logs = info.logs.slice(-20).reverse();
    for (const log of logs) {
      const logLine = document.createElement('div');
      logLine.style.color = this.getLogLevelColor(log.level);
      logLine.textContent = this.formatLogEntry(log);
      logsList.appendChild(logLine);
    }
    logsSection.appendChild(logsList);
    this.detailsPanel.appendChild(logsSection);
  }
}

// ========================================
// Logs Debug Page
// ========================================

/**
 * Debug page showing all logs across systems and entities.
 */
export class LogsDebugPage extends BaseDebugPage {
  private logsContainer?: HTMLPreElement;
  private filterInput?: HTMLInputElement;
  private showClient: boolean = true;
  private showServer: boolean = true;
  private showErrors: boolean = true;
  private showWarns: boolean = true;
  private showInfo: boolean = true;
  private showLogs: boolean = true;
  private refreshInterval?: ReturnType<typeof setInterval>;

  constructor(debugSystem: ClientDebugSystem) {
    super(
      { id: 'logs', label: 'Logs', priority: 5 },
      debugSystem
    );
  }

  createElement(): HTMLElement {
    const container = this.createContainer();

    // Filters
    const filterBar = document.createElement('div');
    Object.assign(filterBar.style, {
      marginBottom: '10px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
    });

    // Text filter
    this.filterInput = document.createElement('input');
    this.filterInput.placeholder = 'Filter...';
    Object.assign(this.filterInput.style, {
      flex: '1',
      minWidth: '100px',
      padding: '5px',
      backgroundColor: '#333',
      color: '#fff',
      border: '1px solid #555',
      borderRadius: '4px',
    });
    this.filterInput.oninput = () => this.refresh();
    filterBar.appendChild(this.filterInput);

    // Source filters
    filterBar.appendChild(this.createFilterCheckbox('Client', this.showClient, (v) => { this.showClient = v; this.refresh(); }));
    filterBar.appendChild(this.createFilterCheckbox('Server', this.showServer, (v) => { this.showServer = v; this.refresh(); }));

    // Level filters
    filterBar.appendChild(this.createFilterCheckbox('Error', this.showErrors, (v) => { this.showErrors = v; this.refresh(); }, '#f55'));
    filterBar.appendChild(this.createFilterCheckbox('Warn', this.showWarns, (v) => { this.showWarns = v; this.refresh(); }, '#fa0'));
    filterBar.appendChild(this.createFilterCheckbox('Info', this.showInfo, (v) => { this.showInfo = v; this.refresh(); }, '#5af'));
    filterBar.appendChild(this.createFilterCheckbox('Log', this.showLogs, (v) => { this.showLogs = v; this.refresh(); }, '#aaa'));

    container.appendChild(filterBar);

    // Logs display
    this.logsContainer = document.createElement('pre');
    Object.assign(this.logsContainer.style, {
      backgroundColor: '#1a1a1a',
      padding: '10px',
      borderRadius: '4px',
      overflow: 'auto',
      flex: '1',
      fontSize: '11px',
      whiteSpace: 'pre-wrap',
      maxHeight: 'calc(100vh - 150px)',
    });
    container.appendChild(this.logsContainer);

    this.element = container;
    this.refresh();
    return container;
  }

  private createFilterCheckbox(
    label: string,
    initial: boolean,
    onChange: (v: boolean) => void,
    color?: string
  ): HTMLLabelElement {
    const labelEl = document.createElement('label');
    Object.assign(labelEl.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
      fontSize: '11px',
      color: color || '#fff',
    });

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = initial;
    checkbox.onchange = () => onChange(checkbox.checked);

    labelEl.appendChild(checkbox);
    labelEl.appendChild(document.createTextNode(label));
    return labelEl;
  }

  onActivate(): void {
    this.refreshInterval = setInterval(() => this.refresh(), 500);
  }

  onDeactivate(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private refresh(): void {
    if (!this.logsContainer) return;

    // Collect all logs
    const allLogs: DebugLogEntry[] = [];

    // System logs
    const systems = this.debugSystem.getAllSystemDebugInfo();
    for (const info of Object.values(systems)) {
      allLogs.push(...info.logs);
    }

    // Entity logs
    const entities = this.debugSystem.getAllEntityDebugInfo();
    for (const info of Object.values(entities)) {
      allLogs.push(...info.logs);
    }

    // Sort by timestamp
    allLogs.sort((a, b) => b.timestamp - a.timestamp);

    // Filter
    const filterText = this.filterInput?.value.toLowerCase() || '';
    const filtered = allLogs.filter(log => {
      // Source filter
      if (log.source === 'client' && !this.showClient) return false;
      if (log.source === 'server' && !this.showServer) return false;

      // Level filter
      if (log.level === 'error' && !this.showErrors) return false;
      if (log.level === 'warn' && !this.showWarns) return false;
      if (log.level === 'info' && !this.showInfo) return false;
      if (log.level === 'log' && !this.showLogs) return false;

      // Text filter
      if (filterText) {
        const logText = [
          log.systemName,
          ...log.args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a))
        ].join(' ').toLowerCase();
        if (!logText.includes(filterText)) return false;
      }

      return true;
    });

    // Render (limit to 200)
    this.logsContainer.innerHTML = '';
    for (const log of filtered.slice(0, 200)) {
      const logLine = document.createElement('div');
      logLine.style.color = this.getLogLevelColor(log.level);
      
      const sourceIcon = log.source === 'server' ? '🖥' : '💻';
      const time = new Date(log.timestamp).toLocaleTimeString();
      const level = log.level.toUpperCase().padEnd(5);
      const message = log.args.map(a => 
        typeof a === 'object' ? JSON.stringify(a) : String(a)
      ).join(' ');
      
      logLine.textContent = `${sourceIcon} [${time}] ${level} [${log.systemName}] ${message}`;
      this.logsContainer.appendChild(logLine);
    }
  }
}

// ========================================
// Areas Debug Page
// ========================================

/**
 * Debug page showing area information.
 */
export class AreasDebugPage extends BaseDebugPage {
  private contentContainer?: HTMLDivElement;
  private refreshInterval?: ReturnType<typeof setInterval>;

  constructor(debugSystem: ClientDebugSystem) {
    super(
      { id: 'areas', label: 'Areas', priority: 30 },
      debugSystem
    );
  }

  createElement(): HTMLElement {
    const container = this.createContainer();

    this.contentContainer = document.createElement('div');
    container.appendChild(this.contentContainer);

    this.element = container;
    this.refresh();
    return container;
  }

  onActivate(): void {
    this.refreshInterval = setInterval(() => this.refresh(), 1000);
  }

  onDeactivate(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  private refresh(): void {
    if (!this.contentContainer) return;
    this.contentContainer.innerHTML = '';

    // Server meta
    const serverSection = this.createSection('Server Area');
    const meta = this.debugSystem.serverMeta;
    this.createKeyValue('Area ID', meta.areaId || 'N/A', serverSection);
    this.createKeyValue('Area Type', meta.areaType || 'N/A', serverSection);
    this.contentContainer.appendChild(serverSection);

    // Client $areaData (if available)
    const clientSection = this.createSection('Client Area Data');
    const areaData = (this.debugSystem as any).$areaData;
    if (areaData && Object.keys(areaData).length > 0) {
      const pre = document.createElement('pre');
      Object.assign(pre.style, {
        backgroundColor: '#1a1a1a',
        padding: '10px',
        borderRadius: '4px',
        fontSize: '11px',
        maxHeight: '200px',
        overflow: 'auto',
      });
      pre.textContent = JSON.stringify(areaData, null, 2);
      clientSection.appendChild(pre);
    } else {
      const p = document.createElement('p');
      p.textContent = 'No area data available';
      p.style.color = '#888';
      clientSection.appendChild(p);
    }
    this.contentContainer.appendChild(clientSection);
  }
}

// ========================================
// Factory function to create default pages
// ========================================

/**
 * Create all default debug pages.
 */
export function createDefaultDebugPages(debugSystem: ClientDebugSystem): IDebugPage[] {
  return [
    new LogsDebugPage(debugSystem),
    new SystemsDebugPage(debugSystem),
    new EntitiesDebugPage(debugSystem),
    new AreasDebugPage(debugSystem),
  ];
}
